/**
 * Utility functions for handling image URLs in the super admin portal
 */

import apiClient from '../services/api';

/**
 * Convert a relative image path to an accessible URL using the backend API
 * @param {string} imagePath - The relative image path from the database
 * @returns {Promise<string|null>} - Promise that resolves to the accessible URL or null if no path provided
 */
export const getImageUrl = async (imagePath) => {
  if (!imagePath || imagePath.trim() === '') {
    return null;
  }

  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  try {
    // Call the backend API to get image URL
    // This handles MinIO authentication and returns an accessible URL
    const response = await apiClient.post('/api/super-admin/images/url', { imageUrl: imagePath });

    if (response.data && response.data.accessUrl) {
      console.log(`Successfully obtained image URL for: ${imagePath}`);
      return response.data.accessUrl;
    } else {
      console.warn(`Failed to get image URL for: ${imagePath}`);
      return null;
    }
  } catch (error) {
    console.warn(`Error getting image URL for: ${imagePath}`, error);
    return null;
  }
};

/**
 * Synchronous version that returns a placeholder for immediate use
 * Use this when you need an immediate URL for display purposes
 * @param {string} imagePath - The relative image path from the database
 * @returns {string|null} - A placeholder URL or null
 */
export const getImageUrlSync = (imagePath) => {
  if (!imagePath || imagePath.trim() === '') {
    return null;
  }

  // If it's already a full URL, return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // Return a placeholder that will be replaced by async call
  // This is a temporary URL that will be updated once the presigned URL is obtained
  return `${window.location.origin}/images/${imagePath}`;
};

/**
 * Get image URL with error handling for React components
 * @param {string} imagePath - The relative image path from the database
 * @returns {string|null} - The accessible URL or null if no path provided
 */
export const getImageUrlSafe = (imagePath) => {
  try {
    return getImageUrl(imagePath);
  } catch (error) {
    console.warn('Error converting image path:', imagePath, error);
    return null;
  }
};

/**
 * Check if an image path exists and is valid
 * @param {string} imagePath - The relative image path from the database
 * @returns {boolean} - True if the path exists and is valid
 */
export const hasValidImagePath = (imagePath) => {
  return imagePath && imagePath.trim() !== '' && !imagePath.startsWith('http');
};

/**
 * Extract filename from image path for display purposes
 * @param {string} imagePath - The relative image path from the database
 * @returns {string} - The filename or the original path if extraction fails
 */
export const getImageFilename = (imagePath) => {
  if (!imagePath) return '';
  
  try {
    const parts = imagePath.split('/');
    return parts[parts.length - 1] || imagePath;
  } catch (error) {
    return imagePath;
  }
};
